import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { BookOpen, Check, Lock } from "lucide-react";
import { learnerCourseState } from "utils/learnerCourses";
import { Course, CourseProgressData, AssignedCourseSummary } from "types";
import { DEFAULT_FLOW_IMAGE_URL, isSignedImageUrlUsable, resolveSignedImageUrl } from "utils/signedImageUrl";
import { forgetPersistentImage, getPersistentImageUrl } from "utils/persistentImageCache";

export const courseUrl = (id: number) => `/training/${id}`;
export const percent = (value: number | undefined) =>
  Math.min(100, Math.max(0, Math.round(value || 0)));
export const courseEligibility = (
  summary: AssignedCourseSummary,
  progress?: CourseProgressData,
) => summary.eligibility ?? progress?.eligibility;
export const courseState = learnerCourseState;
export const stateLabels = {
  unavailable: "Progress unavailable",
  locked: "Locked",
  completed: "Completed",
  in_progress: "In progress",
  not_started: "Not started",
};
export const courseImage = (course?: Course) =>
  course?.image_signed_url?.trim() ||
  (course?.media?.media_type === "image"
    ? course.media.url?.trim() || course.media.storage_key?.trim()
    : null) ||
  course?.image_url?.trim() || undefined;
export const courseCounts = (progress?: CourseProgressData, summary?: AssignedCourseSummary) => {
  const lessons = progress?.flows?.length ?? progress?.total_flows ?? summary?.total_flows ?? 0;
  const populatedChapterIds = new Set(
    progress?.flows?.map((flow) => flow.chapter_id),
  );
  const chapters = progress ? (
    progress?.chapters ?? progress?.course?.chapters ?? []
  ).filter((chapter) => populatedChapterIds.has(chapter.id)).length : summary?.chapter_count ?? 0;
  return `${chapters ? `${chapters} chapter${chapters === 1 ? "" : "s"} · ` : ""}${lessons} module${lessons === 1 ? "" : "s"}`;
};

const coverThemes = ["sunrise", "hills", "arches", "orbits", "waves", "leaves"] as const;
const nonInitialCharacters = new RegExp("[^\\p{L}\\p{N}]", "gu");

const courseInitials = (name: string) => {
  const acronym = name.match(/\(([A-Z][A-Z0-9]{1,2})\)\s*$/)?.[1];
  if (acronym) return acronym;

  const words = name
    .trim()
    .split(/\s+/)
    .map((word) => word.replace(nonInitialCharacters, ""))
    .filter(Boolean);
  return (
    words.length > 1
      ? Array.from(words[0])[0] + Array.from(words[words.length - 1])[0]
      : Array.from(words[0] || "").slice(0, 2).join("")
  ).toUpperCase();
};

interface CourseCoverProps {
  courseId: number;
  name: string;
  source?: string | null;
  small?: boolean;
  fallbackArtwork?: "abstract" | "mountains";
}

export function CourseCover(props: CourseCoverProps) {
  // A changed source starts a new request. Events from its old image cannot
  // mark the replacement as loaded or failed.
  return <CourseCoverContent key={props.source?.trim() || ""} {...props} />;
}

function CourseCoverContent({
  courseId,
  name,
  source,
  small = false,
  fallbackArtwork = "abstract",
}: CourseCoverProps) {
  const themeIndex = Math.abs(Math.trunc(courseId)) % coverThemes.length;
  const theme = coverThemes[themeIndex];
  const imageSource = source?.trim();
  const hasPhoto = Boolean(
    imageSource &&
      !["default.png", "/default.png", DEFAULT_FLOW_IMAGE_URL].includes(imageSource),
  );
  // Keep a stable HTTP-cache URL for unchanged revisions, even when the API
  // supplies a new signature. A cache miss can retry that fresh signature.
  const [directImage] = useState(() => hasPhoto && imageSource && (
    isSignedImageUrlUsable(imageSource) ||
    ["/static/", "/assets/", "./", "../", "data:", "blob:"].some((prefix) => imageSource.startsWith(prefix))
  ) ? getPersistentImageUrl(imageSource) : null);
  const [resolvedImage, setResolvedImage] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const networkImageRef = useRef<string | null>(directImage ? imageSource ?? null : null);
  useEffect(() => {
    let cancelled = false;
    const resolveImage = async (source: string) => {
      try {
        const resolvedUrl = (isSignedImageUrlUsable(source)
          ? source
          : await resolveSignedImageUrl(source)).trim();
        if (cancelled) return;
        if (!resolvedUrl || ["default.png", "/default.png", DEFAULT_FLOW_IMAGE_URL].includes(resolvedUrl)) {
          setFailed(true);
          return;
        }
        networkImageRef.current = resolvedUrl;
        setResolvedImage(getPersistentImageUrl(resolvedUrl));
      } catch {
        if (!cancelled) setFailed(true);
      }
    };
    if (hasPhoto && imageSource && !directImage) void resolveImage(imageSource);
    return () => {
      cancelled = true;
    };
  }, [hasPhoto, imageSource, directImage]);
  const handleImageError = useCallback(() => {
    if (networkImageRef.current && imageRef.current?.getAttribute("src") !== networkImageRef.current) {
      // Browser eviction can expose an expired retained URL. Retry the current
      // download URL once and retain it for subsequent visits.
      forgetPersistentImage(networkImageRef.current);
      getPersistentImageUrl(networkImageRef.current);
      setLoaded(false);
      // Storage writes may be blocked; recovery must use the fresh URL even if
      // the best-effort alias replacement above could not be saved.
      setResolvedImage(networkImageRef.current);
    } else {
      setFailed(true);
    }
  }, []);
  const showFallback = !hasPhoto || failed;
  const coverImage = showFallback ? null : resolvedImage || directImage;
  useLayoutEffect(() => {
    // Cached images may have completed before React receives their load event.
    const element = imageRef.current;
    if (element?.complete) {
      if (element.naturalWidth > 0) setLoaded(true);
      else handleImageError();
    }
  }, [coverImage, handleImageError]);
  const initials = courseInitials(name);
  const artwork = [
    <g key="sunrise">
      <circle className="cover-accent" cx="247" cy="57" r="43" />
      <path className="cover-soft" d="M-30 140Q100 30 210 151T380 146V210H-30Z" />
      <path className="cover-line" d="M-20 160Q120 57 218 162M-20 172Q120 69 218 174" />
    </g>,
    <g key="hills">
      <circle className="cover-soft" cx="63" cy="36" r="37" />
      <path className="cover-accent" d="M-35 163Q60 19 176 145T370 95V200H-35Z" />
      <path className="cover-soft" d="M108 190Q235 16 365 100V200Z" />
      <path className="cover-line" d="M163 179Q265 37 363 118" />
    </g>,
    <g key="arches" fill="none">
      <path className="cover-line" style={{ strokeWidth: 25 }} d="M219 198V76a68 68 0 0 0-136 0v122" />
      <path className="cover-accent" d="M323 183V49a51 51 0 0 0-102 0v134Z" />
      <path className="cover-line" d="M303 185V50a31 31 0 0 0-62 0v135" />
      <circle className="cover-soft" cx="21" cy="151" r="51" />
    </g>,
    <g key="orbits">
      <circle className="cover-soft" cx="268" cy="133" r="91" />
      <g className="cover-line" fill="none">
        <ellipse cx="241" cy="125" rx="120" ry="47" transform="rotate(-35 241 125)" />
        <ellipse cx="241" cy="125" rx="140" ry="63" transform="rotate(-35 241 125)" />
      </g>
      <circle className="cover-accent" cx="52" cy="39" r="24" />
      <circle className="cover-accent" cx="271" cy="59" r="8" />
    </g>,
    <g key="waves">
      <path className="cover-soft" d="M-20 15Q53 87 151 34T345 48V-10H-20Z" />
      <path className="cover-accent" d="M-20 134Q72 72 166 142T345 126V200H-20Z" />
      <path className="cover-line" d="M-20 114Q72 52 166 122T345 106M-20 125Q72 63 166 133T345 117" />
      <circle className="cover-soft" cx="261" cy="88" r="20" />
    </g>,
    <g key="leaves">
      <path className="cover-accent" d="M233 176Q163 62 290 19Q337 115 233 176Z" />
      <path className="cover-soft" d="M53 176Q-22 103 18 48Q112 58 53 176Z" />
      <path className="cover-line" d="M221 192L281 48M49 187L29 76M254 115L285 106M263 91L249 65" />
      <circle className="cover-soft" cx="193" cy="20" r="17" />
    </g>,
  ];
  return (
    <div
      className={`course-cover course-cover-${theme}${small ? " course-cover-small" : ""}${showFallback ? "" : " course-cover-photo"}`}
      data-testid="course-cover"
      data-image-state={showFallback ? "fallback" : loaded ? "loaded" : "loading"}
      aria-hidden="true"
    >
      {showFallback && <>
        <svg data-testid="course-cover-art" className="course-cover-art" viewBox="0 0 320 168" preserveAspectRatio="xMidYMid slice" focusable="false">
          {fallbackArtwork === "mountains" ? <>
            <circle fill="#efb7a0" cx="235" cy="45" r="19" />
            <path className="cover-soft" d="M0 168L87 64L149 109L209 53L320 168Z" />
            <path className="cover-accent" d="M0 168L63 130L142 168Z" />
          </> : artwork[themeIndex]}
        </svg>
        {fallbackArtwork === "abstract" && <span data-testid="course-cover-initials" className="course-cover-initials">{initials || <BookOpen size={30} data-testid="course-cover-empty-name" />}</span>}
      </>}
      {coverImage && (
        <img
          key={coverImage}
          ref={imageRef}
          data-testid="course-cover-image"
          src={coverImage}
          alt=""
          style={{ opacity: loaded ? 1 : 0 }}
          onLoad={() => setLoaded(true)}
          onError={handleImageError}
        />
      )}
    </div>
  );
}

export function CourseProgressBar({
  progress,
  summary,
  labelMode = "module-count",
}: {
  progress?: CourseProgressData;
  summary: AssignedCourseSummary;
  labelMode?: "module-count" | "percentage";
}) {
  const value = percent(progress?.progress_percent ?? summary.progress_percent);
  const completed =
    progress?.flows.filter((flow) => Boolean(flow.completed_at)).length ?? summary.completed_flows ?? 0;
  const total = progress?.flows.length ?? progress?.total_flows ?? summary.total_flows;
  return (
    <div className="course-progress">
      <div
        className={`course-progress-track${value === 100 ? " is-complete" : ""}`}
        role="progressbar"
        aria-label={`${summary.name} progress`}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <span style={{ width: `${value}%` }} />
      </div>
      <span>
        {labelMode === "module-count" && total !== undefined
          ? `${completed} of ${total} module${total === 1 ? "" : "s"}`
          : `${value}% complete`}
      </span>
    </div>
  );
}

export function CourseStatus({
  summary,
  progress,
}: {
  summary: AssignedCourseSummary;
  progress?: CourseProgressData;
}) {
  const state = courseState(summary, progress);
  return (
    <span className={`course-status course-status-${state}`}>
      {state === "locked" ? (
        <Lock size={13} />
      ) : state === "completed" ? (
        <Check size={14} />
      ) : null}
      {stateLabels[state]}
    </span>
  );
}
